import { useEffect, useRef, useState } from 'react'

import useWindowSize from '@/hooks/useWindowSize'
import { useWidgetStore } from '@/stores/useWidgetStore'
import { WidgetBreakpointTab } from '@/types'
import { logger } from '@/utils'
import { getProperBreakpoint } from '@/utils/widget-utils'

/**
 * Hook to get the current chat widget breakpoint based on screen width.
 *
 * Uses debouncing (100ms by default) to prevent excessive re-renders on window resize.
 * Only updates when the actual breakpoint changes, not on every pixel change.
 *
 * @param delay - Debounce delay in milliseconds (default: 100ms)
 * @returns The current WidgetBreakpointTab
 */
export function useWidgetBreakpoint(delay = 100): WidgetBreakpointTab {
  const { screenSize } = useWindowSize(delay)
  const isFloatingWidgetMode = useWidgetStore((state) => state.isFloatingWidgetMode)
  const initialBreakpoint = getProperBreakpoint(screenSize.width)
  const prevBreakpointRef = useRef<WidgetBreakpointTab | null>(initialBreakpoint)
  const [breakpoint, setBreakpoint] = useState<WidgetBreakpointTab>(initialBreakpoint)

  useEffect(() => {
    const newBreakpoint = getProperBreakpoint(screenSize.width)

    if (newBreakpoint !== prevBreakpointRef.current) {
      logger.log('breakpoint changed', newBreakpoint)
      prevBreakpointRef.current = newBreakpoint

      if (isFloatingWidgetMode) {
        setBreakpoint(WidgetBreakpointTab.Phone)
      } else {
        setBreakpoint(newBreakpoint)
      }
    }
  }, [screenSize.width, isFloatingWidgetMode])

  return breakpoint
}
